import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApi } from "../../../hooks/useApi";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Car,
  Search,
  MapPin,
  Activity,
  Radio,
  Smartphone,
  ArrowRight,
  Battery,
  Signal,
  Gauge,
  Clock,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Truck,
  Navigation,
  Zap,
  AlertCircle,
  Loader2,
  RefreshCw,
} from "lucide-react";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
  PointElement,
  LineElement,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
  PointElement,
  LineElement
);

const MapDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { get } = useApi();

  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const isAdminRoute = location.pathname.startsWith("/admin");
  const mapRoutePrefix = isAdminRoute ? "/admin/map" : "/customer/map";

  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    const q = searchQuery.toLowerCase();
    let filtered = vehicles.filter(
      (v) =>
        v.vehicle_number?.toLowerCase().includes(q) ||
        v.vehicle_name?.toLowerCase().includes(q) ||
        v.device_id?.toLowerCase().includes(q) ||
        v.imei_number?.toLowerCase().includes(q)
    );

    if (selectedStatus !== "all") {
      filtered = filtered.filter((v) => v.status === selectedStatus);
    }

    setFilteredVehicles(filtered);
  }, [searchQuery, vehicles, selectedStatus]);

  const fetchVehicles = async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const res = await get("/customer/my-vehicles?per_page=1000&page=1");
      if (res?.data) {
        const normalized = res.data.map((v) => ({
          vehicle_id: v.vehicle_id || v.id,
          vehicle_number: v.vehicle_number || "",
          vehicle_name: v.vehicle_name || "",
          device_id: v.device_id || "",
          imei_number: v.imei_number || "",
          status: v.status || "active",
          battery: Math.floor(Math.random() * 100),
          signal: Math.floor(Math.random() * 100),
          lastUpdate: new Date(Date.now() - Math.random() * 3600000).toISOString(),
        }));
        setVehicles(normalized);
        setFilteredVehicles(normalized);
      }
    } catch (err) {
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleVehicleClick = (vehicle) => {
    const deviceId = vehicle.device_id;
    if (!deviceId) return;

    const params = new URLSearchParams(location.search);
    const useHalfUI = params.get("half") === "1" && !isAdminRoute;

    const targetPath = useHalfUI
      ? `${mapRoutePrefix}/vehicle-half/${encodeURIComponent(deviceId)}`
      : `${mapRoutePrefix}/vehicle/${encodeURIComponent(deviceId)}`;

    navigate(targetPath, {
      state: {
        vehicle_number: vehicle.vehicle_number,
        vehicle_name: vehicle.vehicle_name,
      },
    });
  };

  const stats = {
    total: vehicles.length,
    active: vehicles.filter((v) => v.status === "active").length,
    withDevices: vehicles.filter((v) => v.device_id).length,
    online: vehicles.filter((v) => v.signal > 50).length,
    inactive: vehicles.filter((v) => v.status === "inactive").length,
  };

  const statusChart = {
    labels: ["Active", "Inactive"],
    datasets: [
      {
        data: [stats.active, stats.inactive],
        backgroundColor: ["#10b981", "#ef4444"],
        borderWidth: 0,
      },
    ],
  };

  const deviceChart = {
    labels: ["Connected", "Not Connected"],
    datasets: [
      {
        data: [stats.withDevices, stats.total - stats.withDevices],
        backgroundColor: ["#6366f1", "#e2e8f0"],
        borderWidth: 0,
      },
    ],
  };

  const activityData = {
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    datasets: [
      {
        label: "Active Hours",
        data: [65, 78, 52, 84, 71, 43, 38],
        backgroundColor: "rgba(99, 102, 241, 0.1)",
        borderColor: "#6366f1",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: { size: 12 },
        bodyFont: { size: 11 },
        cornerRadius: 8,
      },
    },
  };

  const lineChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
        ticks: {
          font: { size: 10 },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: { size: 10 },
        },
      },
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
      },
    },
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "from-green-500 to-green-600";
      case "inactive":
        return "from-red-500 to-red-600";
      default:
        return "from-amber-500 to-amber-600";
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-50 text-green-700 border-green-200";
      case "inactive":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-amber-50 text-amber-700 border-amber-200";
    }
  };

  const getSignalStrength = (signal) => {
    if (signal > 70) return { color: "text-green-500", label: "Strong" };
    if (signal > 40) return { color: "text-amber-500", label: "Medium" };
    return { color: "text-red-500", label: "Weak" };
  };

  const getBatteryColor = (battery) => {
    if (battery > 50) return "text-green-500";
    if (battery > 20) return "text-amber-500";
    return "text-red-500";
  };

  const formatLastUpdate = (timestamp) => {
    const minutes = Math.floor((Date.now() - new Date(timestamp)) / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  return (
    <>
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-foreground">Fleet Dashboard</h2>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Monitor and track all vehicles in real-time
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchVehicles(true)}
            disabled={refreshing}
            className="border-1 border-muted-foreground/20 flex-shrink-0 touch-manipulation text-foreground"
          >
            <RefreshCw
              className={`h-4 w-4 sm:mr-2 ${refreshing ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
        <Badge
          variant="outline"
          className="w-fit px-2.5 py-1 border-1 border-muted-foreground/20 bg-muted/30"
        >
          <Clock className="w-3 h-3 mr-1.5" />
          <span className="text-[10px] sm:text-xs">Last sync: 2 min ago</span>
        </Badge>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="relative flex-1">
          <Input
            placeholder="Search vehicles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-11 sm:h-10 rounded-xl border-1 border-muted-foreground/20 bg-background text-sm touch-manipulation text-foreground"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
          {["all", "active", "inactive"].map((status) => (
            <Button
              key={status}
              variant={selectedStatus === status ? "default" : "outline"}
              onClick={() => setSelectedStatus(status)}
              className={`rounded-xl capitalize border-1 flex-shrink-0 min-w-[80px] h-10 touch-manipulation text-foreground ${
                selectedStatus === status
                  ? "border-muted-foreground/10"
                  : "border-muted-foreground/10"
              }`}
             
            >
              {status}
            </Button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {[
          {
            label: "Total",
            labelFull: "Total Vehicles",
            value: stats.total,
            icon: Truck,
            bgGradient: "from-blue-500 to-blue-600",
            change: "+12%",
          },
          {
            label: "Active",
            labelFull: "Active Vehicles",
            value: stats.active,
            icon: Activity,
            bgGradient: "from-green-500 to-green-600",
            change: "+5%",
          },
          {
            label: "Devices",
            labelFull: "Vehicles with Devices",
            value: stats.withDevices,
            icon: Radio,
            bgGradient: "from-purple-500 to-purple-600",
            change: "+8%",
          },
          {
            label: "Inactive",
            labelFull: "Inactive Vehicles",
            value: stats.inactive,
            icon: AlertCircle,
            bgGradient: "from-red-500 to-red-600",
            change: "-2%",
          },
        ].map((card, i) => {
          const Icon = card.icon;
          return (
            <Card
              key={i}
              className={`group relative overflow-hidden border-1 border-muted-foreground/20 bg-muted/50 hover:shadow-lg transition-shadow duration-300 ${
                loading ? "animate-pulse" : ""
              }`}
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <div
                className={`absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br ${card.bgGradient} opacity-10 rounded-full blur-2xl`}
              ></div>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-sm font-medium text-muted-foreground mb-1.5 sm:mb-2 truncate">
                      <span className="sm:hidden">{card.label}</span>
                      <span className="hidden sm:inline">{card.labelFull}</span>
                    </p>
                    {loading ? (
                      <div className="h-6 sm:h-8 w-12 sm:w-16 bg-muted/70 rounded animate-pulse"></div>
                    ) : (
                      <>
                        <h3 className="text-lg sm:text-xl font-bold text-foreground mb-0.5 sm:mb-1">
                          {card.value}
                        </h3>
                        <div className="flex items-center gap-1 sm:gap-1.5 mt-0.5 sm:mt-1">
                          <TrendingUp className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-green-500 flex-shrink-0" />
                          <span className="text-[9px] sm:text-xs text-muted-foreground">
                            {card.change}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <div
                    className={`p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-gradient-to-br ${card.bgGradient} text-white shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}
                  >
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <Card className="border-1 border-muted-foreground/20 bg-muted/30 hover:shadow-lg transition-shadow duration-300 bg-muted/50">
          <CardHeader className="pb-3 sm:pb-6">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                  <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />
                  Vehicle Status
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1">
                  Active vs Inactive
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-48 sm:h-64 bg-muted/50 rounded-lg animate-pulse"></div>
            ) : (
              <>
                <div className="h-48 sm:h-64">
                  <Doughnut
                    data={statusChart}
                    options={{
                      ...chartOptions,
                      cutout: "70%",
                    }}
                  />
                </div>
                <div className="flex justify-center gap-4 sm:gap-6 mt-3 sm:mt-4 flex-wrap">
                  {statusChart.labels.map((label, i) => (
                    <div key={i} className="flex items-center gap-1.5 sm:gap-2">
                      <div
                        className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor:
                            statusChart.datasets[0].backgroundColor[i],
                        }}
                      />
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="border-1 border-muted-foreground/20 bg-muted/30 hover:shadow-lg transition-shadow duration-300 bg-muted/50">
          <CardHeader className="pb-3 sm:pb-6">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                  <Radio className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500" />
                  Device Connection
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1">
                  Connected vs Not Connected
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-48 sm:h-64 bg-muted/50 rounded-lg animate-pulse"></div>
            ) : (
              <>
                <div className="h-48 sm:h-64">
                  <Doughnut
                    data={deviceChart}
                    options={{
                      ...chartOptions,
                      cutout: "70%",
                    }}
                  />
                </div>
                <div className="flex justify-center gap-4 sm:gap-6 mt-3 sm:mt-4 flex-wrap">
                  {deviceChart.labels.map((label, i) => (
                    <div key={i} className="flex items-center gap-1.5 sm:gap-2">
                      <div
                        className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor:
                            deviceChart.datasets[0].backgroundColor[i],
                        }}
                      />
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Weekly Activity Chart */}
      <Card className="border-1 border-muted-foreground/20 bg-muted/30 hover:shadow-lg transition-shadow duration-300 bg-muted/50">
        <CardHeader className="pb-3 sm:pb-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
                Weekly Activity
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1">
                Vehicle usage hours over the week
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-48 sm:h-64 bg-muted/50 rounded-lg animate-pulse"></div>
          ) : (
            <div className="h-48 sm:h-64">
              <Line data={activityData} options={lineChartOptions} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vehicles Grid */}
      <Card className="border-1 border-muted-foreground/20 bg-muted/30 hover:shadow-lg transition-shadow duration-300 bg-muted/50">
        <CardHeader className="pb-3 sm:pb-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                <Car className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
                All Vehicles
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1">
                {filteredVehicles.length} vehicles found
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card
                  key={i}
                  className="border-1 border-muted-foreground/20 bg-muted/30 animate-pulse"
                >
                  <CardContent className="p-4 sm:p-5">
                    <div className="h-4 bg-muted/70 rounded w-3/4 mb-3"></div>
                    <div className="h-3 bg-muted/70 rounded w-1/2 mb-4"></div>
                    <div className="space-y-2 mb-4">
                      <div className="h-3 bg-muted/70 rounded w-full"></div>
                      <div className="h-3 bg-muted/70 rounded w-full"></div>
                    </div>
                    <div className="h-10 sm:h-8 bg-muted/70 rounded"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {filteredVehicles.map((v, index) => {
                  const signalInfo = getSignalStrength(v.signal);
                  return (
                    <Card
                      key={v.vehicle_id}
                      className={`group border-1 border-muted-foreground/20 bg-background/50 hover:bg-background hover:shadow-md transition-all duration-200 cursor-pointer animate-in slide-in-from-left-4 overflow-hidden touch-manipulation active:scale-[0.98]`}
                      style={{ animationDelay: `${index * 50}ms` }}
                      onClick={() => handleVehicleClick(v)}
                    >
                      {/* Status Indicator Bar */}
                      <div
                        className={`h-1.5 w-full bg-gradient-to-r ${getStatusColor(
                          v.status
                        )}`}
                      />
                      <CardContent className="p-4 sm:p-5 space-y-3 sm:space-y-4">
                        {/* Header */}
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm text-foreground truncate group-hover:text-primary transition-colors">
                              {v.vehicle_number || "Unnamed Vehicle"}
                            </h3>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {v.vehicle_name || "No name"}
                            </p>
                          </div>
                          <Badge
                            variant="outline"
                            className={`ml-2 flex-shrink-0 border-1 ${getStatusBadgeColor(
                              v.status
                            )}`}
                          >
                            {v.status === "active" ? (
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                            ) : (
                              <XCircle className="w-3 h-3 mr-1" />
                            )}
                            {v.status}
                          </Badge>
                        </div>

                        {/* Device Info */}
                        <div className="space-y-2">
                          {v.imei_number && (
                            <div className="flex items-center text-xs text-muted-foreground">
                              <Smartphone
                                size={14}
                                className="mr-2 flex-shrink-0 text-muted-foreground/60"
                              />
                              <span className="font-mono truncate">
                                {v.imei_number}
                              </span>
                            </div>
                          )}
                        </div>

                       

                        {/* Last Update */}
                        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-muted-foreground/10">
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-3 h-3" />
                            Last update: {formatLastUpdate(v.lastUpdate)}
                          </span>
                        </div>

                        {/* Action Button */}
                        <Button
                          variant="outline"
                          className="w-full h-11 sm:h-10 rounded-xl border-1 border-muted-foreground/20 hover:border-primary hover:bg-primary/5 hover:text-primary transition-all group-hover:shadow-sm touch-manipulation text-sm"
                        >
                          <MapPin size={16} className="mr-2" />
                          View on Map
                          <ArrowRight
                            size={16}
                            className="ml-2 group-hover:translate-x-1 transition-transform"
                          />
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {filteredVehicles.length === 0 && !loading && (
                <div className="text-center py-8 sm:py-12">
                  <div className="bg-muted/50 rounded-full w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                    <Search className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground/60" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1">
                    No vehicles found
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Try adjusting your search or filters
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default MapDashboard;
